import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useCustomerSearch } from '@/api/customers';
import { useTeams } from '@/api/teams';
import { useCreateTicket } from '@/api/tickets';
import { useAllUsers } from '@/api/users';
import type { CustomerSummary } from '@/api/types';
import { Avatar, Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { humanize } from '@/lib/format';
import { CHANNELS, CHANNEL_LABEL, PRIORITIES, PRIORITY_LABEL } from './meta';

const schema = z.object({
  customerName: z.string().trim().max(120),
  customerEmail: z.union([z.literal(''), z.string().trim().email('Enter a valid email')]),
  subject: z.string().trim().min(3, 'At least 3 characters').max(200),
  body: z.string().trim().min(1, 'Describe the request').max(20_000),
  channel: z.enum(['email', 'chat', 'app', 'whatsapp', 'api']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  teamId: z.string(),
  assigneeId: z.string(),
  tags: z.string(),
});
type FormValues = z.infer<typeof schema>;

export function NewTicketModal({
  onClose,
  customer: preset,
}: {
  onClose: () => void;
  customer?: CustomerSummary;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, can } = useAuth();
  const create = useCreateTicket();
  const teams = useTeams();
  const users = useAllUsers();
  const [customer, setCustomer] = useState<CustomerSummary | null>(preset ?? null);
  const [query, setQuery] = useState('');
  const search = useCustomerSearch(query);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerName: '',
      customerEmail: '',
      subject: '',
      body: '',
      channel: 'email',
      priority: 'normal',
      teamId: '',
      assigneeId: '',
      tags: '',
    },
  });

  const onSubmit = handleSubmit((v) => {
    if (!customer && !v.customerName) {
      setError('customerName', { message: 'Pick a customer or enter a name' });
      return;
    }
    create.mutate(
      {
        ...(customer
          ? { customerId: customer.id }
          : { customer: { name: v.customerName, email: v.customerEmail || null } }),
        subject: v.subject,
        body: v.body,
        channel: v.channel,
        priority: v.priority,
        teamId: v.teamId || null,
        assigneeId: v.assigneeId || null,
        tags: v.tags
          .split(/[\s,]+/)
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
      },
      {
        onSuccess: (t) => {
          toast.success(`Ticket #${t.number} created`);
          onClose();
          navigate(`/inbox/${t.id}`);
        },
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          if (fields['customer.email']) setError('customerEmail', { message: fields['customer.email'] });
          else toast.error(e.message);
        },
      },
    );
  });

  const assignees = (users.data ?? []).filter((u) => can('assign', 'Ticket') || u.id === user.id);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="New ticket"
      description="Log a request on behalf of a customer (phone call, DM, walk-in…)."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="ticket-form" loading={create.isPending}>
            Create ticket
          </Button>
        </>
      }
    >
      <form id="ticket-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium text-slate-700">Customer</legend>
          {customer ? (
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5">
              <Avatar name={customer.name} src={customer.avatarUrl} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{customer.name}</p>
                <p className="truncate text-xs text-slate-500">{customer.email ?? humanize(customer.type)}</p>
              </div>
              {!preset && (
                <button
                  type="button"
                  onClick={() => setCustomer(null)}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Change customer"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <>
              <Input
                aria-label="Search customers"
                placeholder="Search existing customers by name, email or phone…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query.trim().length >= 2 && (
                <ul className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
                  {(search.data ?? []).map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setCustomer(c)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <Avatar name={c.name} src={c.avatarUrl} size="sm" />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{c.name}</span>
                          <span className="block truncate text-xs text-slate-500">{c.email}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                  {search.data?.length === 0 && (
                    <li className="px-3 py-2 text-sm text-slate-500">
                      No match — add them as a new customer below.
                    </li>
                  )}
                </ul>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="New customer name"
                  error={errors.customerName?.message}
                  {...register('customerName')}
                />
                <Input
                  label="Email"
                  type="email"
                  hint="Optional — we match on email"
                  error={errors.customerEmail?.message}
                  {...register('customerEmail')}
                />
              </div>
            </>
          )}
        </fieldset>

        <Input label="Subject" required error={errors.subject?.message} {...register('subject')} />
        <Textarea
          label="What does the customer need?"
          required
          rows={4}
          error={errors.body?.message}
          {...register('body')}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Channel" {...register('channel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </Select>
          <Select label="Priority" {...register('priority')}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </Select>
          <Select label="Team" {...register('teamId')}>
            <option value="">No team</option>
            {(teams.data ?? [])
              .filter((t) => t.isActive)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </Select>
          <Select label="Assignee" {...register('assigneeId')}>
            <option value="">Unassigned</option>
            {assignees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.id === user.id ? `${u.name} (me)` : u.name}
              </option>
            ))}
          </Select>
        </div>
        <Input label="Tags" placeholder="payout, instagram" hint="Comma separated" {...register('tags')} />
      </form>
    </Modal>
  );
}
